import React, { useMemo, useState } from "react";
import styles from "./ProjectManagementModal.module.scss";
import ModalOverlay from "../../../UI/ModalOverlay/ModalOverlay.tsx";
import { ReactComponent as ArrowIcon } from "../../../../assets/svg/arrowDownIcon.svg";
import { ReactComponent as Check } from "../../../../assets/svg/checkmark.svg";
import SubProjectBudgets from "../../SubProjects/SubProjectBudgets";
import { Pencil, Trash2, UserCheck, UserRoundX, UserRound, Building2, Briefcase, X } from "lucide-react";
import { usePostulationsFilter, type Postulation } from "../../../../hooks/usePostulationsFilter.ts";
import { POSTULATION_STATUS, POSTULATION_ACTION_TYPES, POSTULATION_SECTIONS } from "../../../../constants/postulations.ts";
import { formatUserName } from "../../../../utils/userHelpers.ts";
import type { Project, SubProject } from "../../../../types/project.ts";

interface ProjectManagementModalProps {
  proyecto: Project;
  postulacionesDeProyecto: Postulation[];
  budgets?: Record<number | string, any[]>;
  onClose: () => void;
  handleAccessRequest: (permisoId: number, status: string, solicitudType: string) => void;
  handleEditProject: () => void;
  handleDeleteProject: () => void;
}

const ProjectManagementModal: React.FC<ProjectManagementModalProps> = ({
  proyecto,
  postulacionesDeProyecto,
  budgets = {},
  onClose,
  handleAccessRequest,
  handleEditProject,
  handleDeleteProject,
}) => {
  const filteredPostulations = usePostulationsFilter(postulacionesDeProyecto);
  const totalBudgetsCount = useMemo(() => {
    if (!proyecto?.subproyecto || proyecto.subproyecto.length === 0) return 0;

    return proyecto.subproyecto.reduce((total, subProject) => {
      const subProjectBudgets = budgets[subProject.id] || [];
      return total + subProjectBudgets.length;
    }, 0);
  }, [proyecto?.subproyecto, budgets]);

  const [activeTab, setActiveTab] = useState<"postulations" | "budgets">("postulations");
  const [sectionVisibility, setSectionVisibility] = useState<Record<number, boolean>>({
    [POSTULATION_SECTIONS.RECEIVED]: true,
  });

  const toggleSection = (index: number) => {
    setSectionVisibility((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };


  const renderPostulationItem = (postulation: Postulation, index: number) => {
    const userName = formatUserName(postulation.user);
    const userType = postulation.user.type === "COMPANY" ? "Empresa" : "Profesional";
    const userTypeIcon = postulation.user.type === "COMPANY" ? <Building2 size={12} /> : <UserRound size={12} />;
    const profession = postulation.user.profession || "Profesión no especificada";

    return (
      <div className={styles.postulationItem} key={`${postulation.id}-${index}`}>
        <div className={styles.postulationUser}>
          <img
            src={postulation.user.image_profile}
            alt="Imagen de perfil"
            className={styles.postulationUserImage}
          />
          <div className={styles.postulationUserInfo}>
            <div className={styles.postulationUserHeader}>
              <p className={styles.postulationUserName}>{userName}</p>
              <span className={styles.postulationUserType}>
                {userTypeIcon}
                {userType}
              </span>
            </div>
            <p className={styles.postulationUserProfession}>
              <Briefcase size={12} />
              {profession}
            </p>
          </div>
        </div>
        <div className={styles.postulationStatus}>
          {postulation.status === POSTULATION_STATUS.SEND ? (
            <div className={styles.postulationStatusActions}>
              <button
                className={styles.postulationStatusAccept}
                onClick={() =>
                  handleAccessRequest(
                    parseInt(postulation.id.toString()),
                    POSTULATION_STATUS.ALLOWED,
                    POSTULATION_ACTION_TYPES.ACCEPTED
                  )
                }
                aria-label="Aceptar postulación"
              >
                <Check height={14} width={14} />
                <span>Aceptar</span>
              </button>
              <button
                className={styles.postulationStatusDeny}
                onClick={() =>
                  handleAccessRequest(
                    parseInt(postulation.id.toString()),
                    POSTULATION_STATUS.NEGATED,
                    POSTULATION_ACTION_TYPES.NEGATED
                  )
                }
                aria-label="Rechazar postulación"
              >
                <X height={14} width={14} />
                <span>Rechazar</span>
              </button>
            </div>
          ) : postulation.status === POSTULATION_STATUS.ALLOWED ? (
            <div className={styles.postulationStatusBadge}>
              <UserCheck size={16} />
              <span className={styles.postulationStatusAccepted}>Aceptada</span>
            </div>
          ) : (
            <div className={styles.postulationStatusBadge}>
              <UserRoundX size={16} />
              <span className={styles.postulationStatusDenied}>Rechazada</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPostulationSection = (
    title: string,
    postulations: Postulation[],
    sectionIndex: number,
    count: number
  ) => {
    const expanded = !!sectionVisibility[sectionIndex];

    return (
      <div className={styles.sectionGroup} key={sectionIndex}>
        <button
          type="button"
          className={styles.sectionToggle}
          onClick={() => toggleSection(sectionIndex)}
          aria-expanded={expanded}
        >
          <span className={styles.sectionToggleLabel}>{title}</span>
          <span className={styles.sectionToggleMeta}>
            {count > 0 && <span className={styles.sectionToggleBadge}>{count}</span>}
            <ArrowIcon className={expanded ? styles.arrowIconDown : styles.arrowIconRight} height={14} width={14} />
          </span>
        </button>
        {expanded && postulations.length > 0 && (
          <div className={styles.postulationList}>
            {postulations.map((postulation, index) => renderPostulationItem(postulation, index))}
          </div>
        )}
        {expanded && postulations.length === 0 && (
          <p className={styles.emptyState}>No hay registros en esta categoría.</p>
        )}
      </div>
    );
  };

  const renderPostulations = () => (
    <div className={styles.tabPanel}>
      {renderPostulationSection(
        "Recibidas",
        filteredPostulations.received,
        POSTULATION_SECTIONS.RECEIVED,
        filteredPostulations.counts.received
      )}
      {renderPostulationSection(
        "Aceptadas",
        filteredPostulations.accepted,
        POSTULATION_SECTIONS.ACCEPTED,
        filteredPostulations.counts.accepted
      )}
      {renderPostulationSection(
        "Rechazadas",
        filteredPostulations.denied,
        POSTULATION_SECTIONS.DENIED,
        filteredPostulations.counts.denied
      )}
    </div>
  );

  const renderBudgets = () => (
    <div className={styles.tabPanel}>
      {proyecto?.subproyecto && proyecto.subproyecto.length > 0 ? (
        <div className={styles.budgetsList}>
          {proyecto.subproyecto.map((subProject: SubProject, index: number) => (
            <SubProjectBudgets subproject={subProject} key={subProject.id || index} />
          ))}
        </div>
      ) : (
        <p className={styles.emptyState}>No hay cotizaciones registradas.</p>
      )}
    </div>
  );

  return (
    <ModalOverlay isOpen={true} onClose={onClose}>
      <div className={styles.modalWrapper}>
        <div className={styles.modal}>
          <header className={styles.header}>
            <div className={styles.headerInfo}>
              <h2>Gestión del proyecto</h2>
              <span className={styles.headerSubtitle}>{proyecto?.name}</span>
            </div>
            <div className={styles.headerActions}>
              <button type="button" className={`${styles.headerButton} ${styles.headerButtonPrimary}`} onClick={handleEditProject}>
                <Pencil size={16} />
                <span>Editar proyecto</span>
              </button>
              <button type="button" className={`${styles.headerButton} ${styles.headerButtonDanger}`} onClick={handleDeleteProject}>
                <Trash2 size={16} />
                <span>Borrar proyecto</span>
              </button>
            </div>
            <button type="button" className={styles.headerClose} onClick={onClose} aria-label="Cerrar">
              <X size={18} />
            </button>
          </header>

        <div className={styles.mobileActions}>
          <button type="button" className={`${styles.mobileActionButton} ${styles.mobileActionPrimary}`} onClick={handleEditProject}>
            <Pencil size={14} />
            <span>Editar</span>
          </button>
          <button type="button" className={`${styles.mobileActionButton} ${styles.mobileActionDanger}`} onClick={handleDeleteProject}>
            <Trash2 size={14} />
            <span>Borrar</span>
          </button>
        </div>

        <div className={styles.tabs}>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === "postulations" ? styles.tabButtonActive : ""}`}
            onClick={() => setActiveTab("postulations")}
          >
            Postulaciones
            {filteredPostulations.counts.received > 0 && (
              <span className={styles.tabBadge}>{filteredPostulations.counts.received}</span>
            )}
          </button>
          <button
            type="button"
            className={`${styles.tabButton} ${activeTab === "budgets" ? styles.tabButtonActive : ""}`}
            onClick={() => setActiveTab("budgets")}
          >
            Presupuestos
            {totalBudgetsCount > 0 && <span className={styles.tabBadge}>{totalBudgetsCount}</span>}
          </button>
        </div>

        <div className={styles.content}>
          <div className={styles.contentScrollArea}>
            {activeTab === "postulations" ? renderPostulations() : renderBudgets()}
          </div>
        </div>


        </div>
      </div>
    </ModalOverlay>
  );
};

export default ProjectManagementModal;
